import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import api from "../utils/api";

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      authLoading: true,

      setAuth: (user, token, refreshToken) => {
        // Header sofort setzen
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        set({
          user,
          token,
          refreshToken: refreshToken || get().refreshToken,
          isAuthenticated: true,
          authLoading: false,
        });
      },

      logout: () => {
        delete api.defaults.headers.common.Authorization;
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          authLoading: false,
        });
      },

      refreshSession: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        const { data } = await api.post(
          "/auth/refresh",
          { refresh_token: refreshToken },
          { _skipAuthRefresh: true }
        );

        get().setAuth(data.user, data.access_token, data.refresh_token);
        return data.access_token;
      },

      initializeAuth: async () => {
        const { token, refreshToken } = get();
        set({ authLoading: true });

        if (!token && !refreshToken) {
          set({ authLoading: false });
          return;
        }

        // Safety: falls Interceptor nicht greift
        if (token) {
          api.defaults.headers.common.Authorization = `Bearer ${token}`;
        } else {
          delete api.defaults.headers.common.Authorization;
        }

        try {
          const { data } = await api.get("/auth/me");
          set({ user: data, isAuthenticated: true, authLoading: false });
        } catch (error) {
          const status = error?.response?.status;
          console.error("Failed to restore session:", status, error?.message);

          console.log("data:", error?.response?.data); // <-- hier steht, was fehlt

          if (status === 401) {
            // Versuche per Refresh-Token wieder einzuloggen
            try {
              const newToken = await get().refreshSession();
              api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
              const { data } = await api.get("/auth/me");
              set({ user: data, isAuthenticated: true, authLoading: false });
              return;
            } catch (refreshError) {
              // Nur bei echter Unauthorized alles löschen
              delete api.defaults.headers.common.Authorization;
              set({
                user: null,
                token: null,
                refreshToken: null,
                isAuthenticated: false,
                authLoading: false,
              });
            }
          } else {
            // Netzfehler o.ä.: Token behalten
            set((s) => ({
              ...s,
              isAuthenticated: !!s.token,
              authLoading: false,
            }));
          }
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => {
        // SSR-Guard
        if (typeof window === "undefined") return undefined;
        return localStorage;
      }),
      partialize: (s) => ({
        user: s.user,
        token: s.token,
        refreshToken: s.refreshToken,
        isAuthenticated: s.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        // before rehydrate (optional)
        return (state, error) => {
          // after rehydrate
          if (error) {
            console.error("rehydration error:", error);
            return;
          }
          if (state?.token) {
            // Header setzen, dann Session prüfen
            api.defaults.headers.common.Authorization = `Bearer ${state.token}`;
          }
          state?.initializeAuth?.();
        };
      },
    }
  )
);

export default useAuthStore;

// Optional: Tabs/Fenster live synchronisieren
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "auth-storage") {
      // @ts-ignore
      useAuthStore.persist?.rehydrate?.();
    }
  });
}
